import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { API_CONFIG } from "@/config/api";
import { Button } from "@/components/ui/Button";
import { setPortalAuth } from "@/core/auth/storage";
import { resolvePostLoginTarget } from "@/core/auth/portalSurface";
import type { LoginWorkspace } from "../loginCode";
import { LOGIN_HEADING, LOGIN_PRIMARY, LOGIN_TEXT } from "../loginStyles";
import { WorkspacePickFunnel } from "../WorkspacePickFunnel";
import { LoginPage, LoginPending } from "../components/LoginPage";

type Status = "verifying" | "pick" | "error";

interface SessionPayload {
  accessToken: string;
  refreshToken: string;
  clientId: string;
  clientName?: string;
  role?: string;
}

/**
 * Landing page for the emailed one-time login link (DD-026):
 * ``/auth/link?lt=<secret>[&redirect=...]``. The email never decides the
 * workspace or surface — a single-workspace link logs straight in; a
 * multi-workspace link shows the same role -> client funnel as the code
 * login. The ?redirect= deep link is honored only when it matches the
 * workspace and role the user ends up in (resolvePostLoginTarget).
 */
export const LinkLoginRoute: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const secret = searchParams.get("lt");
  const rawRedirect = searchParams.get("redirect");

  const [status, setStatus] = useState<Status>("verifying");
  const [workspaces, setWorkspaces] = useState<LoginWorkspace[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pickError, setPickError] = useState<string | null>(null);
  const requested = useRef(false);

  const completeLogin = useCallback(
    (data: SessionPayload) => {
      setPortalAuth(data.clientId, {
        token: data.accessToken,
        refreshToken: data.refreshToken,
        role: data.role || "client",
        clientName: data.clientName,
      });
      navigate(
        resolvePostLoginTarget(rawRedirect, data.clientId, data.role || "client"),
        { replace: true }
      );
    },
    [navigate, rawRedirect]
  );

  const verify = useCallback(
    async (clientId?: string): Promise<"ok" | "pick" | "error"> => {
      const body: { token: string; clientId?: string } = {
        token: secret || "",
      };
      if (clientId) body.clientId = clientId;
      const resp = await fetch(
        `${API_CONFIG.URL}/portal/auth/verify-login-link`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }
      );
      const data = await resp.json();
      if (!resp.ok || !data.success) return "error";
      if (data.clients) {
        setWorkspaces(data.clients);
        return "pick";
      }
      completeLogin(data);
      return "ok";
    },
    [secret, completeLogin]
  );

  useEffect(() => {
    if (requested.current) return;
    requested.current = true;
    if (!secret) {
      setStatus("error");
      return;
    }
    verify()
      .then((outcome) => {
        if (outcome === "pick") setStatus("pick");
        if (outcome === "error") setStatus("error");
      })
      .catch(() => setStatus("error"));
  }, [secret, verify]);

  const handlePick = async (clientId: string) => {
    setPickError(null);
    setIsSubmitting(true);
    try {
      const outcome = await verify(clientId);
      if (outcome === "error") {
        setPickError("Link non più valido. Richiedi un nuovo accesso.");
      }
    } catch {
      setPickError("Impossibile contattare il server. Riprova.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <LoginPage>
      {status === "verifying" && <LoginPending label="Accesso in corso..." />}

      {status === "pick" && (
        <>
          <div className="mb-(--login-gap)">
            <h1 className={LOGIN_HEADING}>Accedi al portale</h1>
          </div>
          <WorkspacePickFunnel
            workspaces={workspaces}
            onPick={handlePick}
            disabled={isSubmitting}
            error={pickError}
          />
        </>
      )}

      {status === "error" && (
        <>
          <div className="mb-(--login-gap)">
            <h1 className={LOGIN_HEADING}>Link non valido</h1>
            <p className={LOGIN_TEXT}>
              Il link è scaduto o è già stato usato. Richiedi un nuovo
              accesso dalla pagina di login.
            </p>
          </div>
          <Button
            type="button"
            variant="primary"
            size="lg"
            className={LOGIN_PRIMARY}
            onClick={() => navigate("/portal/login", { replace: true })}
          >
            Vai al login
          </Button>
        </>
      )}
    </LoginPage>
  );
};
